import React, { useState, useRef, useEffect } from "react";
import { analyzePhotos } from "../lib/analyze";

type AnalyzeResult = any;

type Props = {
  onResult: (res: AnalyzeResult) => void;
  initial?: {
    marca?: string;
    modelo?: string;
    ano?: string;
    fipe?: string;
  };
  maxFiles?: number;
  maxFileSizeMB?: number;
};

export default function UploadPhotos({ onResult, initial = {}, maxFiles = 8, maxFileSizeMB = 5 }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [fields, setFields] = useState({
    marca: initial.marca || "",
    modelo: initial.modelo || "",
    ano: initial.ano || "",
    fipe: initial.fipe || "",
    danoSimulado: "0",
  });

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p));
    };
  }, [previews]);

  function pushError(msg: string) {
    setErrors((prev) => [...prev, msg]);
  }

  function handleFiles(selected: FileList | null) {
    setErrors([]);
    if (!selected) return;
    const arr = Array.from(selected);
    if (arr.length > maxFiles) {
      pushError(`Máximo ${maxFiles} fotos. As primeiras ${maxFiles} serão usadas.`);
    }
    const limited = arr.slice(0, maxFiles);
    const valid: File[] = [];
    for (const f of limited) {
      if (!f.type.startsWith("image/")) {
        pushError(`Arquivo "${f.name}" ignorado (não é imagem).`);
        continue;
      }
      const sizeMB = f.size / 1024 / 1024;
      if (sizeMB > maxFileSizeMB) {
        pushError(`"${f.name}" excede ${maxFileSizeMB} MB e foi ignorado.`);
        continue;
      }
      valid.push(f);
    }
    previews.forEach((p) => URL.revokeObjectURL(p));
    setFiles(valid);
    setPreviews(valid.map((f) => URL.createObjectURL(f)));
  }

  async function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    setErrors([]);
    if (files.length === 0) {
      pushError("Adicione ao menos 1 foto do veículo.");
      return;
    }
    setLoading(true);
    try {
      const json = await analyzePhotos({
        files,
        fields: {
          marca: fields.marca,
          modelo: fields.modelo,
          ano: fields.ano,
          fipe: fields.fipe || "0",
          danoSimulado: fields.danoSimulado,
        },
      });
      onResult(json);
    } catch (err: any) {
      console.error("Upload error:", err);
      pushError(err?.message ?? "Erro ao enviar imagens.");
    } finally {
      setLoading(false);
    }
  }

  function handleClear() {
    previews.forEach((p) => URL.revokeObjectURL(p));
    setFiles([]);
    setPreviews([]);
    setErrors([]);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="w-full max-w-3xl bg-card/80 dark:bg-slate-900/60 rounded-lg p-6 shadow-sm border border-border">
      <h3 className="text-lg font-semibold mb-3">Fotos do veículo</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          className="block w-full rounded border px-3 py-2 bg-transparent"
          placeholder="Marca"
          value={fields.marca}
          onChange={(e) => setFields({ ...fields, marca: e.target.value })}
        />
        <input
          className="block w-full rounded border px-3 py-2 bg-transparent"
          placeholder="Modelo"
          value={fields.modelo}
          onChange={(e) => setFields({ ...fields, modelo: e.target.value })}
        />
        <input
          className="block w-full rounded border px-3 py-2 bg-transparent"
          placeholder="Ano"
          value={fields.ano}
          onChange={(e) => setFields({ ...fields, ano: e.target.value })}
        />
        <input
          className="block w-full rounded border px-3 py-2 bg-transparent"
          placeholder="Valor FIPE (R$)"
          value={fields.fipe}
          onChange={(e) => setFields({ ...fields, fipe: e.target.value })}
        />
      </div>

      <label className="mt-4 flex items-center gap-3 cursor-pointer">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          onChange={(e) => handleFiles(e.target.files)}
          className="sr-only"
        />
        <div
          className="px-3 py-2 rounded border hover:bg-slate-50 dark:hover:bg-slate-800"
          role="button"
          aria-label="Selecionar ou tirar fotos"
        >
          Selecionar / Tirar fotos
        </div>
        <div className="text-sm text-muted-foreground">
          Máx {maxFiles} fotos • JPG/PNG • {maxFileSizeMB}MB (arraste arquivos aqui)
        </div>
      </label>

      {errors.length > 0 && (
        <div className="mt-3 space-y-1">
          {errors.map((er, i) => (
            <div key={i} className="text-sm text-red-500">
              {er}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 grid grid-cols-4 gap-2">
        {previews.length === 0 && (
          <div className="col-span-4 text-sm text-muted-foreground">Nenhuma foto selecionada</div>
        )}
        {previews.map((p, i) => (
          <div
            key={i}
            className="w-full h-20 rounded overflow-hidden border flex items-center justify-center bg-black/5 relative group"
          >
            <img src={p} alt={`preview-${i}`} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <label className="text-sm">Nível de dano simulado: {fields.danoSimulado}%</label>
        <input
          type="range"
          min={0}
          max={100}
          value={fields.danoSimulado}
          onChange={(e) => setFields({ ...fields, danoSimulado: e.target.value })}
          className="w-full"
        />
      </div>

      <div className="mt-4 flex gap-3">
        <button
          onClick={() => handleSubmit()}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? "Enviando..." : "Calcular Preço-Alvo"}
        </button>

        <button
          type="button"
          onClick={handleClear}
          className="inline-flex items-center px-3 py-2 rounded border border-border hover:bg-accent"
          disabled={loading}
        >
          Limpar
        </button>
      </div>
    </div>
  );
}
